<template>
  <div class="d-flex flex-column ga-3">
    <div class="d-flex align-center justify-space-between flex-wrap ga-2">
      <div>
        <div class="text-body-2 font-weight-semibold ncrm-text-strong">
          {{ title || $t("ncrm.workflows.nodeSettings.telegramNotify.title") }}
        </div>
        <div class="text-caption text-medium-emphasis mt-1">
          {{ hint || $t("ncrm.workflows.nodeSettings.telegramNotify.hint") }}
        </div>
      </div>
      <v-switch
        v-model="enabledProxy"
        color="primary"
        hide-details
        :label="$t('ncrm.workflows.nodeSettings.telegramNotify.enabled')"
      />
    </div>

    <template v-if="enabledProxy">
      <v-alert
        v-if="!loadingRecipients && recipientOptions.length === 0"
        type="info"
        density="compact"
      >
        {{ $t("ncrm.workflows.nodeSettings.telegramNotify.noRecipients") }}
      </v-alert>
      <v-select
        v-else
        v-model="selectedUserIds"
        :items="recipientOptions"
        :label="$t('ncrm.workflows.nodeSettings.telegramNotify.recipients')"
        :loading="loadingRecipients"
        multiple
        chips
        closable-chips
        hide-details="auto"
        :menu-props="menuProps"
      />
    </template>
  </div>
</template>

<script>
import axios from "axios";

// Reusable Telegram-notify config block. Mutates the passed `config` object
// in place: { enabled: bool, recipientUserIds: number[] }. Used by workflow
// node settings (API import — Sprint 6b) and reusable for future nodes.
export default {
  name: "NcrmWorkflowTelegramNotify",
  props: {
    config: { type: Object, required: true },
    title: { type: String, default: "" },
    hint: { type: String, default: "" },
    // Menu must render above the settings drawer (z-index ~2700); default
    // Vuetify menu z-index (2000) would hide options behind the drawer.
    menuProps: { type: Object, default: () => ({ maxHeight: 320, zIndex: 2710 }) },
  },
  data() {
    return {
      loadingRecipients: false,
      team: [],
    };
  },
  computed: {
    enabledProxy: {
      get() {
        return !!this.config.enabled;
      },
      set(value) {
        this.config.enabled = value;
        if (value && this.team.length === 0) this.fetchRecipients();
      },
    },
    selectedUserIds: {
      get() {
        return Array.isArray(this.config.recipientUserIds) ? this.config.recipientUserIds : [];
      },
      set(value) {
        this.config.recipientUserIds = Array.isArray(value)
          ? value.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v > 0)
          : [];
      },
    },
    recipientOptions() {
      return this.team.map((r) => {
        const name = r.user_name || r.user_email || `User #${r.user_id}`;
        const tg = r.telegram_username ? `@${r.telegram_username}` : `chat ${r.telegram_chat_id}`;
        return { title: `${name} (${tg})`, value: Number(r.user_id) };
      });
    },
  },
  mounted() {
    // Pre-fetch recipients on open so the list is ready the instant the user
    // enables the toggle (avoids the ~1s empty window from the 2 sequential
    // HTTP calls). Safe + cheap; runs once per drawer open.
    this.fetchRecipients();
  },
  methods: {
    async fetchRecipients() {
      this.loadingRecipients = true;
      try {
        const { data: accountsData } = await axios.get("/integrations/telegram/link/accounts");
        const accounts = Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
        if (!accounts.length) {
          this.team = [];
          return;
        }
        const accountId = accounts[0].id;
        const { data } = await axios.get(
          `/integrations/telegram/accounts/${accountId}/recipients`
        );
        this.team = Array.isArray(data?.team) ? data.team : [];
      } catch (_) {
        this.team = [];
      } finally {
        this.loadingRecipients = false;
      }
    },
  },
};
</script>
